import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import guardiansService from "../services/guardiansService";
import s3Service from "../services/s3Service";
import rentsNoticeToQuitService from "../services/rentsNoticeToQuitService";
import rentsService from "../services/rentsService";
import styles from "../assets/guardianShow.module.css";
import Loader from "../components/Loader";
import GuardianDocuments from "../components/GuardianDocuments";
import AddEmploymentModal from "../components/AddEmploymentModal";
import { canHaveChangedPaymentsOnRent } from "../utils/guardianStatusUtils";

// Rent status mapping (matches Laravel config/rents.php status_reverse)
const RENT_STATUS_MAP = {
  0: 'In Progress',
  1: 'Active',
  2: 'Failed',
  3: 'Ended',
  'in_progress': 'In Progress',
  'active': 'Active',
  'failed': 'Failed',
  'ended': 'Ended'
};

// Helper to get rent status label
const getRentStatusLabel = (status) => {
  if (status === null || status === undefined) return '-';
  return RENT_STATUS_MAP[status] || RENT_STATUS_MAP[String(status).toLowerCase()] || String(status);
};

// Helper to get rent status CSS class
const getRentStatusClass = (status, styles) => {
  const statusNum = typeof status === 'number' ? status : parseInt(status);
  const statusStr = typeof status === 'string' ? status.toLowerCase() : '';

  if (statusNum === 1 || statusStr === 'active') return styles.statusBadgeActive;
  if (statusNum === 2 || statusStr === 'failed') return styles.statusBadgeFailed;
  if (statusNum === 3 || statusStr === 'ended') return styles.statusBadgeEnded;
  // 0 = in_progress - use a default style
  return styles.statusBadgeInProgress || styles.statusBadgeEnded;
};

const GuardianShow = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [guardian, setGuardian] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showCriminalModal, setShowCriminalModal] = useState(false);
  const [showEmploymentModal, setShowEmploymentModal] = useState(false);
  const [selectedEmployment, setSelectedEmployment] = useState(null);
  const [showAddEmploymentModal, setShowAddEmploymentModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');

  // Primary email inline edit state
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Alternative email inline edit state
  const [editingAltEmail, setEditingAltEmail] = useState(false);
  const [altEmailValue, setAltEmailValue] = useState('');
  const [savingAltEmail, setSavingAltEmail] = useState(false);
  const [altEmailError, setAltEmailError] = useState('');

  // Inline photo upload
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Image must be under 100MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      const imageKey = await s3Service.uploadImage(file, {
        maxSize: 100 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
      });
      await guardiansService.updateGuardian(id, { image: imageKey });
      toast.success('Photo updated');
      await loadGuardian();
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error(err?.message || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const loadGuardian = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use real API data from backend
      const data = await guardiansService.getGuardianById(id);
      setGuardian(data);

    } catch (err) {
      console.error("Error loading guardian:", err);
      setError("Failed to load guardian details");
      toast.error("Failed to load guardian details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadGuardian();
  }, [loadGuardian]);

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch {
      return dateString;
    }
  };

  const formatGender = (gender) => {
    if (!gender) return '-';
    
    // Gender mapping matching Laravel config
    const genderMap = {
      '1': 'Male',
      '2': 'Female',
      '3': 'Other',
      '4': 'Prefer not to say',
      '5': 'Let me Type*',
      'Male': 'Male',
      'Female': 'Female',
      'Other': 'Other',
      'Prefer not to say': 'Prefer not to say',
      'Let me Type*': 'Let me Type*'
    };
    
    // Convert to string for lookup
    const genderKey = String(gender);
    return genderMap[genderKey] || gender;
  };

  const hasPermission = (permission) => {
    if (!user) return false;

    // If user has permissions array, check it
    if (user.permissions && Array.isArray(user.permissions)) {
      return user.permissions.includes(permission);
    }

    // If user has role_permissions object (from Laravel structure)
    if (user.role_permissions && Array.isArray(user.role_permissions)) {
      return user.role_permissions.some(rp => rp.permission === permission);
    }

    // Fallback: check role-based permissions
    const rolePermissions = {
      'ADMIN': [
        'guardians.edit', 'guardians.payments', 'guardians.assign', 'guardians.destroy',
        'guardians.move', 'property-application.payment.set', 'property-application.documentation.decline',
        'property-application.rent.dismiss', 'property-application.mark-as-paid', 'rents.start',
        'guardians.send-payment-details', 'guardians.send-payment-reminder'
      ],
      'GUARDIAN_OFFICER': [
        'guardians.edit', 'guardians.payments', 'guardians.assign', 'guardians.move',
        'property-application.payment.set', 'property-application.documentation.decline',
        'property-application.rent.dismiss', 'property-application.mark-as-paid', 'rents.start'
      ],
      'GUARDIAN': [],
      'CLIENT': []
    };

    const userRole = user.role?.name || user.role_name || 'GUARDIAN';
    const allowedPermissions = rolePermissions[userRole] || [];

    return allowedPermissions.includes(permission);
  };

  // Primary email edit handlers
  const handleEditEmail = () => {
    setEmailValue(guardian.user?.email || '');
    setEmailError('');
    setEditingEmail(true);
  };

  const handleCancelEmail = () => {
    setEditingEmail(false);
    setEmailValue('');
    setEmailError('');
  };

  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return 'Email address is required';
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    if (guardian.user?.email_alternative && email.toLowerCase() === guardian.user?.email_alternative?.toLowerCase()) {
      return 'Primary email must be different from alternative email';
    }
    return '';
  };

  const handleSaveEmail = async () => {
    const error = validateEmail(emailValue);
    if (error) {
      setEmailError(error);
      return;
    }

    setSavingEmail(true);
    setEmailError('');

    try {
      await guardiansService.updateGuardian(id, {
        email: emailValue.trim()
      });

      // Update local state
      setGuardian(prev => ({
        ...prev,
        user: {
          ...prev.user,
          email: emailValue.trim()
        }
      }));

      setEditingEmail(false);
      toast.success('Email updated successfully');
    } catch (err) {
      console.error('Error updating email:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update email';
      setEmailError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSavingEmail(false);
    }
  };

  // Alternative email edit handlers
  const handleEditAltEmail = () => {
    setAltEmailValue(guardian.user?.email_alternative || '');
    setAltEmailError('');
    setEditingAltEmail(true);
  };

  const handleCancelAltEmail = () => {
    setEditingAltEmail(false);
    setAltEmailValue('');
    setAltEmailError('');
  };

  const validateAltEmail = (email) => {
    if (!email || email.trim() === '') {
      return ''; // Empty is valid (clearing the alternative email)
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    if (email.toLowerCase() === guardian.user?.email?.toLowerCase()) {
      return 'Alternative email must be different from primary email';
    }
    return '';
  };

  const handleSaveAltEmail = async () => {
    const error = validateAltEmail(altEmailValue);
    if (error) {
      setAltEmailError(error);
      return;
    }

    setSavingAltEmail(true);
    setAltEmailError('');

    try {
      await guardiansService.updateGuardian(id, {
        email_alternative: altEmailValue.trim() || null
      });

      // Update local state
      setGuardian(prev => ({
        ...prev,
        user: {
          ...prev.user,
          email_alternative: altEmailValue.trim() || null
        }
      }));

      setEditingAltEmail(false);
      toast.success(altEmailValue.trim() ? 'Alternative email updated successfully' : 'Alternative email removed');
    } catch (err) {
      console.error('Error updating alternative email:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update alternative email';
      setAltEmailError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSavingAltEmail(false);
    }
  };

  // Action handlers
  const handleUnblacklist = async () => {
    try {
      await guardiansService.unblacklistGuardian(id);
      toast.success('Guardian successfully updated');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to unblacklist guardian');
    }
  };

  const handleDecline = async () => {
    try {
      await guardiansService.declineGuardian(id);
      toast.success('Guardian declined successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to decline guardian');
    }
  };

  const handleBlacklist = async () => {
    try {
      await guardiansService.blacklistGuardian(id);
      toast.success('Guardian successfully updated');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to blacklist guardian');
    }
  };

  const handleDeclineDoc = () => {
    window.open(`/property-application/decline-documentation/${id}`, '_blank');
  };

  const handleDismiss = async (rentId) => {
    try {
      await guardiansService.dismissFromProperty(rentId);
      toast.success('Guardian dismissed from property successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to dismiss guardian from property');
    }
  };

  const handleNotInterested = async () => {
    try {
      await guardiansService.markAsNotInterested(id);
      toast.success('Guardian marked as not interested successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to mark guardian as not interested');
    }
  };

  const handleArchive = async () => {
    try {
      await guardiansService.archiveGuardian(id);
      toast.success('Guardian archived successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to archive guardian');
    }
  };

  const handleMarkAsPaid = (rentId) => {
    // Laravel behavior: always navigate without payment ID to mark ALL initial payments at once
    // This matches Laravel's route('initial.mark-as-paid', [$latestRent->id, null])
    window.open(`/payments/mark-as-paid-initial/${rentId}`, '_blank');
  };

  const handleSetToDocumentsRequired = async () => {
    try {
      await guardiansService.setToDocumentsRequired(id);
      toast.success('Guardian set to documents required successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to set guardian to documents required');
    }
  };

  const handleEditEmploymentHistory = (historyId) => {
    window.open(`/employment-history/${historyId}/edit`, '_blank');
  };

  const handleDeleteEmploymentHistory = async (historyId) => {
    try {
      await guardiansService.deleteEmploymentHistory(historyId);
      toast.success('Employment history deleted successfully');
      loadGuardian();
    } catch (error) {
      toast.error('Failed to delete employment history');
    }
  };

  // Removed unused handleEditRentDates to satisfy linter

  const handleShowMedicalHistory = () => {
    setShowMedicalModal(true);
  };

  const handleShowCriminalRecord = () => {
    setShowCriminalModal(true);
  };

  const handleExportDetails = async () => {
    try {
      toast.info('Generating guardian details export...');
      const blob = await guardiansService.downloadGuardianDetails(id);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guardian_${id}_details.xlsx`;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Guardian details exported successfully!');
    } catch (error) {
      console.error('Failed to export guardian details:', error);
      toast.error('Failed to export guardian details. Please try again.');
    }
  };

  const handleCloseMedicalModal = () => {
    setShowMedicalModal(false);
  };

  const handleCloseCriminalModal = () => {
    setShowCriminalModal(false);
  };

  const handleShowEmployment = (employment) => {
    setSelectedEmployment(employment);
    setShowEmploymentModal(true);
  };

  const handleCloseEmploymentModal = () => {
    setShowEmploymentModal(false);
    setSelectedEmployment(null);
  };

  const handleViewProperty = (propertyId) => {
    window.open(`/properties/${propertyId}`, '_blank');
  };

  const handleViewRoom = (roomId) => {
    window.open(`/rooms/${roomId}/show`, '_blank');
  };

  const handleViewLicenseAgreement = async (rentId) => {
    try {
      console.log(`[GUARDIAN_SHOW] Attempting to view signed license agreement for rent ${rentId}`);
      
      // Use rentsService to get presigned URL for direct S3 download
      const presignedUrl = await rentsService.getLicenceDocument(rentId, 'signed_license_agreement');
      
      if (!presignedUrl) {
        throw new Error('No presigned URL returned from server');
      }
      
      // Force download instead of opening in new tab (matching Laravel download behavior)
      console.log(`[GUARDIAN_SHOW] Triggering download for signed license agreement`);
      
      // Fetch file as blob to force download (works for cross-origin URLs)
      const response = await fetch(presignedUrl);
      const blob = await response.blob();
      
      // Create blob URL and download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `signed_license_agreement_${rentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success('Downloading signed license agreement...');
    } catch (error) {
      console.error('[GUARDIAN_SHOW] Error opening license agreement:', error);
      console.error('[GUARDIAN_SHOW] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        rentId: rentId
      });
      
      // Provide user-friendly error messages matching Laravel behavior
      let errorMessage = 'Failed to open license agreement';
      
      if (error.response?.status === 404) {
        if (error.response?.data?.message?.includes("doesn't exist")) {
          errorMessage = "The signed agreement file doesn't exist on server. Please report that to the admin.";
        } else if (error.response?.data?.message?.includes("not found")) {
          errorMessage = "Signed agreement not found. The document may need to be re-uploaded.";
        } else {
          errorMessage = error.response?.data?.message || 'Signed agreement not found';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Log additional debugging information
      if (error.response?.data?.expectedPath) {
        console.error('[GUARDIAN_SHOW] Expected file path:', error.response.data.expectedPath);
        console.error('[GUARDIAN_SHOW] File may need to be re-uploaded to S3');
      }
      
      if (error.response?.data?.filename) {
        console.error('[GUARDIAN_SHOW] Filename from database:', error.response.data.filename);
      }
    }
  };

  const handleViewLicence = async (rentId) => {
    try {
      console.log(`[GUARDIAN_SHOW] Attempting to view license agreement for rent ${rentId}`);
      console.log(`[GUARDIAN_SHOW] API endpoint will be: /rents/${rentId}/licences/license_agreement/view`);
      
      // Use rentsService to get presigned URL for direct S3 download (matching Laravel guardians.licenseAgreement.get route)
      // Laravel: route('guardians.licenseAgreement.get', [$model->id, 'license_agreement'])
      const presignedUrl = await rentsService.getLicenceDocument(rentId, 'license_agreement');
      
      console.log(`[GUARDIAN_SHOW] Received presigned URL:`, presignedUrl ? 'URL received' : 'No URL');
      
      if (!presignedUrl) {
        throw new Error('No presigned URL returned from server');
      }
      
      // Force download instead of opening in new tab (matching Laravel download behavior)
      console.log(`[GUARDIAN_SHOW] Triggering download for license agreement`);
      
      // Fetch file as blob to force download (works for cross-origin URLs)
      const response = await fetch(presignedUrl);
      const blob = await response.blob();
      
      // Create blob URL and download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `license_agreement_${rentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success('Downloading license agreement...');
    } catch (error) {
      console.error('[GUARDIAN_SHOW] Error opening license agreement:', error);
      console.error('[GUARDIAN_SHOW] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        rentId: rentId,
        url: error.config?.url
      });
      
      // Provide user-friendly error messages matching Laravel behavior
      let errorMessage = 'Failed to open license agreement';
      
      if (error.response?.status === 404) {
        console.error(`[GUARDIAN_SHOW] 404 Error - API endpoint not found: ${error.config?.url}`);
        if (error.response?.data?.message?.includes("doesn't exist")) {
          errorMessage = "The license agreement file doesn't exist on server. Please report that to the admin.";
        } else if (error.response?.data?.message?.includes("not found")) {
          errorMessage = "License agreement not found. The document may need to be re-uploaded.";
        } else {
          errorMessage = error.response?.data?.message || 'License agreement not found';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      
      // Log additional debugging information
      if (error.response?.data?.expectedPath) {
        console.error('[GUARDIAN_SHOW] Expected file path:', error.response.data.expectedPath);
        console.error('[GUARDIAN_SHOW] File may need to be re-uploaded to S3');
      }
      
      if (error.response?.data?.filename) {
        console.error('[GUARDIAN_SHOW] Filename from database:', error.response.data.filename);
      }
      
      // IMPORTANT: Prevent any navigation - this is an API call, not a route navigation
      console.log('[GUARDIAN_SHOW] Error handled - no navigation should occur');
    }
  };

  const handleManageLicence = (rentId) => {
    window.open(`/rents/${rentId}/licences/edit`, '_blank');
  };

  const handleViewPayments = (rentId) => {
    window.open(`/rents/${rentId}/payments`, '_blank');
  };

  const handleRestoreRent = async (rentId) => {
    if (!window.confirm("Are you sure you want to restore this rent? It will be moved back to 'In Progress'.")) {
      return;
    }
    try {
      await rentsService.restoreRent(rentId);
      toast.success("You moved the rent back to 'In Progress'.");
      loadGuardian();
    } catch (error) {
      console.error("Error restoring rent:", error);
      toast.error(error.response?.data?.message || "Failed to restore rent");
    }
  };

  const handleChangeRent = (roomId) => {
    if (roomId) {
      // Laravel route: rents.payments.edit with room parameter
      window.open(`/rents/payments/edit/${roomId}`, '_blank');
    } else {
      toast.error("Cannot change rent: Room information is missing");
    }
  };

  const handleEndRent = async (rentId) => {
    if (!window.confirm("Are you sure you want to end this rent? This will mark the guardian as Previous Guardian.")) {
      return;
    }
    try {
      await rentsService.finishRent(rentId);
      toast.success("Rent ended successfully. Guardian status updated to Previous Guardian.");
      loadGuardian();
    } catch (error) {
      console.error("Error ending rent:", error);
      toast.error(error.response?.data?.message || "Failed to end rent");
    }
  };

  const handleManageOfficer = (rentId) => {
    window.open(`/rents/${rentId}/officer/edit`, '_blank');
  };

  const handleRemoveNtq = async (rentId) => {
    if (!rentId) {
      toast.error("No rent found for NTQ removal");
      return;
    }

    if (!window.confirm("Are you sure you want to remove NTQ?")) {
      return;
    }

    try {
      await rentsService.deleteNoticeToQuit(rentId);
      toast.success("NTQ successfully deleted");
      await loadGuardian();
    } catch (error) {
      console.error("Error removing NTQ:", error);
      toast.error(error.response?.data?.message || "Failed to remove NTQ");
    }
  };

  const hasRentEndDatePassed = (rent) => {
    if (!rent?.date_end) return false;
    const endDate = new Date(rent.date_end);
    if (Number.isNaN(endDate.getTime())) return false;
    return endDate < new Date();
  };

  const handleSortTable = (column) => {
    if (sortField === column) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a new field, set it and default to ascending
      setSortField(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderActionButtons = () => {
    if (!guardian) return null;

    const statusId = guardian.status?.id;
    const latestRent = guardian.latest_not_ended_or_failed_rent;
    const isArchived = statusId === 13;
    const isBlacklisted = statusId === 8;

    return (
      <div className="row">
        <div className="col-xs-12">
          <div className={styles.btnGroup}>
            {/* Edit button */}
            {hasPermission('guardians.edit') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/guardians/${id}/edit`, '_blank')}
              >
                Edit
              </button>
            )}

            {/* Payments button */}
            {hasPermission('guardians.payments') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/guardians/${id}/payments`, '_blank')}
              >
                <i className="fa fa-money"></i> Payments
              </button>
            )}

            {/* Unblacklist button */}
            {isBlacklisted && hasPermission('guardians.destroy') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={handleUnblacklist}
                data-confirm-action="Are you sure you want to add unblock guardian?"
              >
                <i className="fa fa-check" aria-hidden="true"></i> Unblacklist
              </button>
            )}

            {/* Not Interested button for Registered status */}
            {statusId === 1 && hasPermission('guardians.edit') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={handleDecline}
                data-confirm-action="Are you sure you want to decline guardian?"
              >
                Not Interested
              </button>
            )}

            {/* Assign button */}
            {guardian.canBeAssigned && hasPermission('guardians.assign') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/rooms/assign/${guardian.id}`, '_blank')}
              >
                Assign
              </button>
            )}

            {/* Blacklist button */}
            {hasPermission('guardians.destroy') && !isBlacklisted && !isArchived && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={handleBlacklist}
                data-confirm-action="Are you sure you want to add guardian to the blacklist?"
              >
                Blacklist
              </button>
            )}

            {/* Move guardian button */}
            {hasPermission('guardians.move') && guardian.canBeMoved && latestRent && (
              <button
                className={styles.btnHead}
                onClick={() => window.open(`/rooms/${latestRent.room?.id}/manage`, '_blank')}
              >
                Move guardian
              </button>
            )}

            {/* Set payment button - Property Assigned status */}
            {/* Laravel exact condition: status_id == 3 && property-application.payment.set && payments.list && latestRent.room */}
            {statusId === 3 && 
             hasPermission('property-application.payment.set') && 
             hasPermission('payments.list') && 
             latestRent?.room && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/property-application/set-payment/${latestRent.room.id}`, '_blank')}
              >
                Set payment
              </button>
            )}

            {/* Decline doc button - Property Assigned status */}
            {statusId === 3 && hasPermission('property-application.documentation.decline') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={handleDeclineDoc}
                data-confirm-action="Are you sure you want to decline documentation?"
              >
                Decline doc
              </button>
            )}

            {/* Dismiss/Not Interested buttons - Documents Required (2) or Vetted (12) */}
            {(statusId === 2 || statusId === 12) && hasPermission('property-application.rent.dismiss') && (
              latestRent ? (
                <button
                  className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                  onClick={() => handleDismiss(latestRent.id)}
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Dismiss from property"
                  data-container="body"
                  data-confirm-action="Are you sure you want to dismiss guardian from property?"
                >
                  Dismiss
                </button>
              ) : (
                <button
                  className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                  onClick={handleNotInterested}
                  data-toggle="tooltip"
                  data-placement="top"
                  title="Mark as not interested"
                  data-container="body"
                  data-confirm-action="Are you sure you want to dismiss guardian?"
                >
                  Not interested
                </button>
              )
            )}

            {/* Archive Guardian button */}
            {!isArchived && hasPermission('guardians.edit') && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={handleArchive}
                data-confirm-action="Are you sure you want to archive this guardian?"
              >
                Archive Guardian
              </button>
            )}

            {/* Mark as paid button - Payment Pending status */}
            {statusId === 4 && hasPermission('property-application.mark-as-paid') && latestRent?.initialPayments?.length > 0 && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => handleMarkAsPaid(latestRent.id)}
              >
                Mark as paid
              </button>
            )}

            {/* Sign License Agreement button - Awaiting Key Collection status */}
            {/* Laravel: Only show if rent exists, is not soft-deleted, and agreement not signed */}
            {statusId === 5 && hasPermission('rents.start') && latestRent && !latestRent.deleted_at && !latestRent.signed_license_agreement && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/property-application/sign-agreement/${latestRent.id}`, '_blank')}
              >
                Sign Licence Agreement
              </button>
            )}

            {/* Start rent button - Awaiting Key Collection status */}
            {statusId === 5 && hasPermission('rents.start') && latestRent?.signed_license_agreement && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => window.open(`/rents/${latestRent.id}/start`, '_blank')}
              >
                Start rent
              </button>
            )}

            {/* End Rent button - Placed status (6) with latest rent */}
            {/* Laravel: Only show if guardian is placed (6) and has latest rent */}
            {/* Backend policy canFinish() validates the rent, so we check for general rent edit permission */}
            {statusId === 6 && hasPermission('rents.edit') && latestRent && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => handleEndRent(latestRent.id)}
                data-confirm-action="Are you sure you want to end this rent? This will mark the guardian as Previous Guardian."
              >
                End Rent
              </button>
            )}

            {/* End Rent button - NTQ status (10) with latest rent */}
            {/* Use guardian.latest_rent instead of latestRent (latest_not_ended_or_failed_rent) */}
            {/* because NTQ guardians may have date_end in the past, which filters them out of latest_not_ended_or_failed_rent */}
            {statusId === 10 && hasPermission('rents.edit') && (latestRent || guardian.latest_rent) && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => handleEndRent((latestRent || guardian.latest_rent).id)}
                data-confirm-action="Are you sure you want to end this rent? This will mark the guardian as Previous Guardian."
              >
                End Rent
              </button>
            )}

            {/* Remove NTQ button - top action row */}
            {statusId === 10 &&
              hasPermission('rents.notice_to_quit.manage') &&
              (latestRent || guardian.latest_rent) && (
                <button
                  className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                  onClick={() => handleRemoveNtq((latestRent || guardian.latest_rent).id)}
                  data-confirm-action="Are you sure you want to remove NTQ?"
                >
                  Remove NTQ
                </button>
              )}

            {/* Send Payment Details button */}
            {hasPermission('guardians.send-payment-details') && guardian.isInPaymentPending && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={async () => {
                  try {
                    await guardiansService.sendPaymentDetails(guardian.id);
                    toast.success('Payment details reminder has been sent!');
                  } catch (err) {
                    console.error('Failed to send payment details:', err);
                    toast.error('Failed to send payment details');
                  }
                }}
              >
                Send Payment Details
              </button>
            )}

            {/* Send Payment Reminder button */}
            {hasPermission('guardians.send-payment-reminder') && guardian.isInPaymentPending && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={async () => {
                  try {
                    await guardiansService.sendPaymentReminder(guardian.id);
                    toast.success('Payment reminder has been sent!');
                  } catch (err) {
                    console.error('Failed to send payment reminder:', err);
                    toast.error('Failed to send payment reminder');
                  }
                }}
              >
                Send Payment Reminder
              </button>
            )}

            {/* Set to Documents Required button - Declined or Archived (laravel-master-2 parity) */}
            {(statusId === 9 || statusId === 13) && (
              <button
                className={`${styles.btnHead} ${styles.hiddenPrintLink}`}
                onClick={() => {
                  if (window.confirm('Are you sure you want to set guardian to Documents Required?')) {
                    handleSetToDocumentsRequired();
                  }
                }}
                data-toggle="tooltip"
                data-placement="top"
                title="Mark as Document Required"
                data-container="body"
                data-confirm-action="Are you sure you want to set guardian to Documents Required?"
              >
                Set to Documents Required
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderGuardianOverview = () => {
    if (!guardian) return null;

    return (
      <div className="row mt-3">
        <div className="col-md-12">
          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
            <h3>Guardian overview</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
            <div className="row">
              <div className="col-sm-6">
                <div className={styles.guardianAvatarWrapper}>
                {guardian.user?.imageUrl ? (
                  <img
                    className={`${styles.guardianAvatar} img-responsive`}
                    src={guardian.user.imageUrl}
                    alt="Guardian Avatar"
                  />
                ) : (
                  <img src="" className={styles.guardianAvatar} alt="" />
                )}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoSelected}
                />
                <div style={{ marginTop: 10, textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto
                      ? 'Uploading...'
                      : guardian.user?.imageUrl
                      ? 'Change Photo'
                      : 'Upload Photo'}
                  </button>
                </div>
              </div>

              <dl className={`${styles.greenList} ${styles.greenListFontFix} ${styles.greenListWithWidth}`}>
                <dt>Guardian ID:</dt>
                <dd>{guardian.unique_guardian_number || '-'}</dd>
                <dt>Name:</dt>
                <dd>{guardian.user?.first_name} {guardian.user?.last_name || '-'}</dd>
                <dt>Phone number:</dt>
                <dd>{guardian.user?.phone_number || '-'}</dd>
                <dt>Mobile number:</dt>
                <dd>{guardian.user?.mobile_number || '-'}</dd>
                <dt>Email:</dt>
                <dd>
                  {editingEmail ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="email"
                          value={emailValue}
                          onChange={(e) => {
                            setEmailValue(e.target.value);
                            setEmailError('');
                          }}
                          placeholder="e.g., email@example.com"
                          style={{
                            padding: '4px 8px',
                            border: emailError ? '1px solid #d9534f' : '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '13px',
                            width: '220px'
                          }}
                          disabled={savingEmail}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveEmail();
                            } else if (e.key === 'Escape') {
                              handleCancelEmail();
                            }
                          }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveEmail}
                          disabled={savingEmail}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#5cb85c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: savingEmail ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {savingEmail ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelEmail}
                          disabled={savingEmail}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      {emailError && (
                        <span style={{ color: '#d9534f', fontSize: '11px' }}>{emailError}</span>
                      )}
                      <span style={{ color: '#888', fontSize: '11px' }}>
                        This is the guardian's primary login email.
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <a href={`mailto:${guardian.user?.email}`} className={`${styles.mailtoInDd} ${styles.hiddenPrintLink}`}>
                        {guardian.user?.email}
                      </a>
                      {hasPermission('guardians.edit') && (
                        <button
                          onClick={handleEditEmail}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#337ab7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                          title="Edit email"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </dd>
                <dt>Alternative Email:</dt>
                <dd>
                  {editingAltEmail ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="email"
                          value={altEmailValue}
                          onChange={(e) => {
                            setAltEmailValue(e.target.value);
                            setAltEmailError('');
                          }}
                          placeholder="e.g., alternate@example.com"
                          style={{
                            padding: '4px 8px',
                            border: altEmailError ? '1px solid #d9534f' : '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '13px',
                            width: '220px'
                          }}
                          disabled={savingAltEmail}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleSaveAltEmail();
                            } else if (e.key === 'Escape') {
                              handleCancelAltEmail();
                            }
                          }}
                          autoFocus
                        />
                        <button
                          onClick={handleSaveAltEmail}
                          disabled={savingAltEmail}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#5cb85c',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: savingAltEmail ? 'not-allowed' : 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          {savingAltEmail ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelAltEmail}
                          disabled={savingAltEmail}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      {altEmailError && (
                        <span style={{ color: '#d9534f', fontSize: '11px' }}>{altEmailError}</span>
                      )}
                      <span style={{ color: '#888', fontSize: '11px' }}>
                        Guardian can login using this email. Leave empty to remove.
                      </span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {guardian.user?.email_alternative ? (
                        <a href={`mailto:${guardian.user?.email_alternative}`} className={`${styles.mailtoInDd} ${styles.hiddenPrintLink}`}>
                          {guardian.user?.email_alternative}
                        </a>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>
                      )}
                      {hasPermission('guardians.edit') && (
                        <button
                          onClick={handleEditAltEmail}
                          style={{
                            padding: '2px 6px',
                            backgroundColor: '#337ab7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                          title="Edit alternative email"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  )}
                </dd>
                {guardian.hearAbout && (
                  <>
                    <dt title="How to did you hear about Global Guardians?">Hear about GG:</dt>
                    <dd>{guardian.hearAbout.name}</dd>
                  </>
                )}
              </dl>
              </div>
              <div className="col-sm-6">
                <dl className={`${styles.greenList} ${styles.greenListFontFix} ${styles.greenListWithWidth}`}>
                <dt>Status:</dt>
                <dd>
        <span
                    className={`${styles.label} ${styles.labelWarning} ${styles.jsTooltip}`}
                    style={{ backgroundColor: guardian.status?.color }}
                    data-toggle="tooltip"
                    data-placement="top"
                    title={guardian.status?.description}
                  >
                    {guardian.status?.name}
        </span>
                </dd>
                <dt>Gender:</dt>
                <dd>{formatGender(guardian.gender)}</dd>
                <dt title="Date of Birth">DoB:</dt>
                <dd>{formatDate(guardian.date_of_birth)}</dd>
                <dt>Nationality:</dt>
                <dd>{guardian.country?.nationality || '-'}</dd>
                <dt>Religion:</dt>
                <dd>{guardian.religion?.name || '-'}</dd>
                <dt title="National Insurance Number">NI:</dt>
                <dd>{guardian.national_insurance_number || '-'}</dd>
                {guardian.next_of_kin && (
                  <>
                    <dt>Next of kin</dt>
                    <dd>{guardian.next_of_kin}</dd>
                  </>
                )}
                </dl>
              </div>
            </div>

            <div className="row" style={{ textAlign: 'right', paddingRight: '40px' }}>
              <div className="col-xs-12">
                {guardian.medical_condition === 1 && (
                  <button
                    className={styles.btnAdd}
                    onClick={handleShowMedicalHistory}
                  >
                    Medical history
                  </button>
                )}

                {guardian.criminal_record === 1 && (
                  <button
                    className={styles.btnAdd}
                    onClick={handleShowCriminalRecord}
                  >
                    Criminal record
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmailPreferences = () => {
    if (!guardian) return null;

    // Email preferences are stored in users table, not guardians table (Laravel: $guardian->user->emails_receive_marketing)
    // Backend returns guardian.user with these fields, so access via guardian.user
    const user = guardian.user || {};

    // Helper function to get display values matching Laravel config
    const getMarketingEmailsValue = (value) => {
      // Laravel config: 0='No', 1='Yes'
      return value === 1 ? 'Yes' : 'No';
    };

    const getUpdatesPreference = (value) => {
      // Laravel config: 0='E-mail', 1='Phone', 2='Letter'
      switch(value) {
        case 0: return 'E-mail';
        case 1: return 'Phone';
        case 2: return 'Letter';
        default: return 'E-mail';
      }
    };

    const getShareDataValue = (value) => {
      // Laravel config: 0='No', 1='Yes'
      return value === 1 ? 'Yes' : 'No';
    };

    return (
      <div className="row">
        <div className="col-md-12">
          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
            <h3>Email preferences</h3>
          </div>
          <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
            <div className="row">
              <div className="col-sm-6">
                <div className={styles.tableResponsive}>
                  <table className={`${styles.table} ${styles.tableStriped} ${styles.tableHover} ${styles.tableCondensed}`}>
                    <tbody>
                      <tr>
                        <td>I agree that I would like to receive promotional offers, available property lists and other updates from Global Guardians</td>
                        <td>
                          <strong>{getMarketingEmailsValue(user.emails_receive_marketing)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>I would like to receive Global Guardians updates by:</td>
                        <td>
                          <strong>{getUpdatesPreference(user.emails_updates_by)}</strong>
                        </td>
                      </tr>
                      <tr>
                        <td>I agree that Global Guardians can share my contact information with other organisations</td>
                        <td>
                          <strong>{getShareDataValue(user.emails_share_data_other_organisations)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmploymentHistory = () => {
    if (!guardian) return null;

    // Sort employment histories based on sortField and sortDirection
    const sortedEmploymentHistories = [...(guardian.employmentHistories || [])].sort((a, b) => {
      if (!sortField) return 0;

      let aValue, bValue;

      switch (sortField) {
        case 'company_name':
          aValue = a.company_name || '';
          bValue = b.company_name || '';
          break;
        case 'date_start':
          aValue = new Date(a.date_start || 0);
          bValue = new Date(b.date_start || 0);
          break;
        case 'date_end':
          // Handle still_in_employment case
          if (a.still_in_employment && !b.still_in_employment) {
            aValue = new Date(); // Current date for still employed
            bValue = new Date(b.date_end || 0);
          } else if (!a.still_in_employment && b.still_in_employment) {
            aValue = new Date(a.date_end || 0);
            bValue = new Date(); // Current date for still employed
          } else if (a.still_in_employment && b.still_in_employment) {
            aValue = new Date(); // Both still employed
            bValue = new Date();
          } else {
            aValue = new Date(a.date_end || 0);
            bValue = new Date(b.date_end || 0);
          }
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle date comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

    return (
      <div className={styles.panelGroup}>
        <div className={styles.panelTitle}>
          <h3 style={{ display: 'inline-block', marginRight: '15px' }}>Employment history</h3>
          <button
            className="btn btn-secondary"
            onClick={() => setShowAddEmploymentModal(true)}
            style={{ marginLeft: '10px' }}
          >
            <i className="glyphicon glyphicon-plus"></i> Add employment
          </button>
        </div>
        <div className={styles.dataTableWrapper}>
          <table
            id={`datatable-employment-history-of-guardian-${guardian.id}`}
            className={`${styles.table} ${styles.tableHover} datatable-employment-history-of-guardian`}
            data-userable_id={guardian.id}
          >
            <thead>
              <tr>
                <th></th>
                <th className={styles.sorting} onClick={() => handleSortTable('company_name')}>
                  Company name <i className={`fa fa-sort${sortField === 'company_name' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('date_start')}>
                  Date start <i className={`fa fa-sort${sortField === 'date_start' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('date_end')}>
                  Date end <i className={`fa fa-sort${sortField === 'date_end' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.actionHead}>Actions</th>
                      </tr>
            </thead>
            <tbody>
              {Array.isArray(sortedEmploymentHistories) && sortedEmploymentHistories.map((history) => (
                <tr key={history.id}>
                  <td></td>
                  <td>{history.company_name}</td>
                  <td>{formatDate(history.date_start)}</td>
                  <td>{history.still_in_employment ? 'Still working there' : formatDate(history.date_end)}</td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleShowEmployment(history)}
                        style={{
                          background: '#ffc107',
                          color: '#212529',
                          border: '1px solid #ffc107',
                          padding: '4px 8px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          marginRight: '4px',
                          fontSize: '12px'
                        }}
                      >
                        Show
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleEditEmploymentHistory(history.id)}
                        style={{
                          background: '#007bff',
                          color: 'white',
                          border: '1px solid #007bff',
                          padding: '4px 8px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          marginRight: '4px',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteEmploymentHistory(history.id)}
                        data-confirm-action="Are you sure you want to delete this employment history?"
                        style={{
                          background: '#dc3545',
                          color: 'white',
                          border: '1px solid #dc3545',
                          padding: '4px 8px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                        </td>
                      </tr>
              )) || (
                      <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>
                    No employment history found.
                        </td>
                      </tr>
              )}
                    </tbody>
                  </table>
                </div>
              </div>
    );
  };

  const renderCurrentProperty = () => {
    if (!guardian) return null;

    const latestRent = guardian.latest_not_ended_or_failed_rent;

    return (
      <>
        <div id="js-rooms-of-property-effect"></div>
        <div className="row">
          <div className="col-md-12">
          <div className={`${styles.panelTitle} ${styles.titleStandard} ${styles.relative}`}>
            <h3 className={styles.mrAuto}>Current property</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                className={`btn btn-secondary ${styles.dPrintNone}`}
                onClick={() => window.open(`/tickets?guardian_id=${id}`, '_blank')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Your tickets
              </button>
              <button
                className={`btn btn-secondary ${styles.dPrintNone}`}
                onClick={() => window.open('/tickets/create', '_blank')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                <i className="glyphicon glyphicon-plus"></i> Submit maintenance request
              </button>
            </div>
          </div>

          {latestRent ? (
            <div className={styles.panelGroup}>
              <div className="row">
            <div className="col-md-4">
              {latestRent.room?.property && (
                <dl className={`${styles.greenList} ${styles.greenListWithWidthSmaller}`}>
                  <dt>Property ID:</dt>
                  <dd>{latestRent.room.property.property_id}</dd>
                  <dt>Property name:</dt>
                  <dd>
                    <a 
                      href={`/properties/${latestRent.room.property.id}`}
                      className={styles.hiddenPrintLink}
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewProperty(latestRent.room.property.id);
                      }}
                    >
                      {latestRent.room.property.name}
                    </a>
                  </dd>
                  <dt>Property address:</dt>
                  <dd>{latestRent.room.property.address_1}</dd>
                  {latestRent.room.property.address_2 && (
                    <>
                      <dt></dt>
                      <dd>{latestRent.room.property.address_2}</dd>
                    </>
                  )}
                  {latestRent.room.property.address_3 && (
                    <>
                      <dt></dt>
                      <dd>{latestRent.room.property.address_3}</dd>
                    </>
                  )}
                  <dt>Postcode:</dt>
                  <dd>{latestRent.room.property.postcode}</dd>
                  {latestRent.room.name && (
                    <>
                      <dt>Room:</dt>
                      <dd>
                        <a 
                          href={`/rooms/${latestRent.room.id}/show`}
                          className={styles.hiddenPrintLink}
                          onClick={(e) => {
                            e.preventDefault();
                            handleViewRoom(latestRent.room.id);
                          }}
                        >
                          {latestRent.room.name}
                        </a>
                      </dd>
                    </>
                  )}
                </dl>
              )}
                </div>

            {Array.isArray(latestRent.room?.assignedGuardiansRents) && latestRent.room?.assignedGuardiansRents?.map((rent) => (
              <div key={rent.id} className="col-md-2">
                <div className={styles.roomOfGuardian}>
                  <div className={styles.guardianInfo}>
                    <strong>{rent.guardian?.user?.first_name} {rent.guardian?.user?.last_name}</strong>
                    <br />
                    <small>{rent.guardian?.unique_guardian_number}</small>
                  </div>
                </div>
              </div>
            ))}

            <div className="col-md-4">
              <div className="row">
                <div className="col-xs-12">
                  {/* Laravel Logic: Show signed agreement ONLY if signed_license_agreement (filename) exists */}
                  {/* Laravel: @if($guardian->latest_not_ended_or_failed_rent && $guardian->latest_not_ended_or_failed_rent->signed_license_agreement) */}
                  {latestRent.signed_license_agreement && (
                    <div className={`property-application-license-agreement ${styles.mt30}`}>
                      <a 
                        href="#"
                        className={styles.hiddenPrintLink}
                        onClick={(e) => {
                          e.preventDefault();
                          handleViewLicenseAgreement(latestRent.id);
                        }}
                      >
                        <div className={styles.btnProperty}>
                          <div className={styles.documentLeft}>
                            <img src="/assets/images/pdf.png" alt="PDF" />
                          </div>
                          <div className={styles.documentRight}>
                            <p className={styles.btnTitle}>Signed Agreement</p>
                            <p className={styles.btnShort}>Click to open & print</p>
                          </div>
                        </div>
                      </a>
                    </div>
                  )}
                </div>
                <div className="clearfix"></div>
              </div>
            </div>
            </div>
            </div>
          ) : (
            <div className={styles.panelGroup}>
              <div className="col-md-12">
                You don't have active property! If you should have contact us, please.
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    );
  };

  const renderAssociatedDocuments = () => {
    if (!guardian) return null;

    // Create array of all documents to display in grid
    const allDocuments = [];

    // Helper function to format document upload date
    const formatDocumentDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      } catch {
        return '';
      }
    };

    // Add passport or "No passport"
    if (guardian.passport?.length > 0) {
      const passportDoc = guardian.passport[0];
      allDocuments.push({
        id: passportDoc.id, // Use actual media ID
        name: 'Passport',
        uploadDate: formatDocumentDate(passportDoc.created_at || passportDoc.uploaded_at),
        url: passportDoc.url,
        type: 'document'
      });
    } else {
      allDocuments.push({
        id: 'no-passport',
        name: 'No passport',
        type: 'no-document'
      });
    }

    // Add bank statements or "No bank statement"
    if (guardian.bankStatements?.length > 0) {
      guardian.bankStatements.forEach((statement, index) => {
        allDocuments.push({
          id: statement.id, // Use actual media ID
          name: `Bank statement ${index + 1}`,
          uploadDate: formatDocumentDate(statement.created_at || statement.uploaded_at),
          url: statement.url,
          type: 'document'
        });
      });
    } else {
      allDocuments.push({
        id: 'no-bank-statement',
        name: 'No bank statement',
        type: 'no-document'
      });
    }

    // Add employment reference or "No employment reference"
    if (guardian.employmentReference?.length > 0) {
      const empRefDoc = guardian.employmentReference[0];
      allDocuments.push({
        id: empRefDoc.id, // Use actual media ID
        name: 'Employment reference',
        uploadDate: formatDocumentDate(empRefDoc.created_at || empRefDoc.uploaded_at),
        url: empRefDoc.url,
        type: 'document'
      });
    } else {
      allDocuments.push({
        id: 'no-employment-ref',
        name: 'No employment reference',
        type: 'no-document'
      });
    }

    // Add payslips or "No payslip"
    if (guardian.payslips?.length > 0) {
      guardian.payslips.forEach((payslip, index) => {
        allDocuments.push({
          id: payslip.id, // Use actual media ID
          name: `Payslip ${index + 1}`,
          uploadDate: formatDocumentDate(payslip.created_at || payslip.uploaded_at),
          url: payslip.url,
          type: 'document'
        });
      });
    } else {
      allDocuments.push({
        id: 'no-payslip',
        name: 'No payslip',
        type: 'no-document'
      });
    }

    return (
      <div className={styles.panelGroup}>
        <div className={styles.panelTitle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <h3>Associated documents</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className={`btn btn-primary ${styles.dPrintNone}`}
              onClick={() => window.open(`/guardians/documents/${guardian.id}`, '_blank')}
              style={{
                backgroundColor: '#1e3a8a',
                borderColor: '#1e3a8a',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Arial, sans-serif'
              }}
            >
              <i className="glyphicon glyphicon-plus" style={{ marginRight: '6px', fontSize: '12px' }}></i> Manage documents
            </button>
            {/* Laravel: Only show document approval/decline buttons for status 2 (documents_required), 11 (documents_uploaded), or 12 (vetted) */}
            {[2, 11, 12].includes(guardian.status_id) && (
              <>
                <button
                  className={`btn btn-warning ${styles.dPrintNone}`}
                  onClick={() => window.open(`/property-application/decline-documentation/${guardian.id}`, '_blank')}
                  style={{
                    backgroundColor: '#ffc107',
                    borderColor: '#ffc107',
                    color: '#212529',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  <i className="glyphicon glyphicon-plus" style={{ marginRight: '6px', fontSize: '12px' }}></i> Decline documents
                </button>
                <button
                  className={`btn btn-success ${styles.dPrintNone}`}
                  onClick={async () => {
                    try {
                      await guardiansService.approveDocuments(guardian.id);
                      toast.success('Documents approved successfully');
                      loadGuardian();
                    } catch (error) {
                      toast.error('Failed to approve documents');
                    }
                  }}
                  style={{
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  Approve documents <i className="glyphicon glyphicon-ok"></i>
                </button>
                <button
                  className={`btn btn-danger ${styles.dPrintNone}`}
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to decline this application?')) {
                      try {
                        await guardiansService.declineApplication(guardian.id);
                        toast.success('Application declined successfully');
                        loadGuardian();
                      } catch (error) {
                        toast.error('Failed to decline application');
                      }
                    }
                  }}
                  style={{
                    backgroundColor: '#dc3545',
                    borderColor: '#dc3545',
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Arial, sans-serif'
                  }}
                >
                  Decline Application <i className="glyphicon glyphicon-remove"></i>
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.documentGrid}>
          {Array.isArray(allDocuments) && allDocuments.map((doc) => (
            <div key={doc.id} className={styles.documentCard}>
              {doc.type === 'document' ? (
                <div
                  className={styles.documentCardContent}
                  onClick={async () => {
                    if (doc.url && doc.type === 'document' && typeof doc.id === 'number') {
                      try {
                        // Use the service method to download the document
                        await guardiansService.downloadGuardianDocument(guardian.id, doc.id);
                        toast.success('Document download initiated');
                      } catch (error) {
                        console.error('Error downloading document:', error);
                        toast.error('Failed to download document');
                      }
                    }
                  }}
                >
                  <div className={styles.documentPdfIcon}>
                    <span className={styles.pdfText}>PDF</span>
                  </div>
                  <div className={styles.documentContent}>
                    <div className={styles.documentTitle}>{doc.name}</div>
                    <div className={styles.documentDate}>Uploaded {doc.uploadDate}</div>
                  </div>
                </div>
              ) : (
                <div className={styles.noDocumentCard}>
                  <div className={styles.noDocumentText}>{doc.name}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderNTQDocuments = () => {
    if (!guardian) return null;

    // Use latest_rent (not latest_not_ended_or_failed_rent) to match Laravel exactly
    // Laravel: $guardian->latest_rent is used for the upload button, but all $ntqLetters are shown
    const latestRent = guardian.latest_rent || guardian.latest_not_ended_or_failed_rent;

    // Laravel: Section is always shown if latest_rent exists (no condition to hide it)
    // Hide section only if no latest_rent exists
    if (!latestRent) {
      console.log('[NTQ Documents] No latest_rent found, hiding section');
      return null;
    }

    // Get NTQ documents from latest_rent (Laravel: loops through all $ntqLetters)
    // Laravel shows ALL ntqLetters for the guardian, not just for latest_rent
    const ntqDocuments = latestRent.ntq_documents || [];
    
    console.log('[NTQ Documents] Rendering section:', {
      hasLatestRent: !!latestRent,
      latestRentId: latestRent?.id,
      ntqDocumentsCount: ntqDocuments.length,
      ntqDocuments: ntqDocuments
    });

    // Laravel always shows the section structure, even if $ntqLetters is empty
    // The @foreach loop just doesn't execute if there are no letters

    return (
      <div className="row">
        <div className="col-md-12">
          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
            <h3 style={{ display: 'inline-block', marginRight: '15px' }}>NTQ documents</h3>
            {/* Laravel: Button only shows if latest_rent exists AND user has permission */}
            {latestRent && hasPermission('rents.notice_to_quit.manage') && (
              <button
                className="btn btn-secondary"
                onClick={() => window.open(`/rents/${latestRent.id}/notice-to-quit/uploadFile`, '_blank')}
                style={{ marginLeft: '10px' }}
              >
                <i className="glyphicon glyphicon-plus"></i> Upload document
              </button>
            )}
          </div>

          <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
            {/* Laravel: NTQ documents displayed as blue cards in grid (same as Associated Documents) */}
            {/* Laravel: @foreach($ntqLetters as $ntqLetter) @if($ntqLetter->letter()) */}
            {/* Laravel: Section always shows, even if $ntqLetters is empty (just shows empty panel-group) */}
            <div className={styles.documentGrid}>
              {ntqDocuments && ntqDocuments.length > 0 ? (
                Array.isArray(ntqDocuments) && ntqDocuments.map((ntqDoc) => (
                  <div key={ntqDoc.id} className={styles.documentCard}>
                    {/* Laravel: Blue document card with PDF icon and "Notice To Quit" text */}
                    <div
                      className={styles.documentCardContent}
                      onClick={async () => {
                        try {
                          // Use presigned URL directly from response if available
                          if (ntqDoc.url) {
                            // Open presigned URL in new tab for download
                            window.open(ntqDoc.url, '_blank');
                            toast.success('NTQ document opened successfully');
                          } else {
                            // Fallback to API call if URL not available
                            await rentsNoticeToQuitService.downloadFile(latestRent.id);
                            toast.success('NTQ document downloaded successfully');
                          }
                        } catch (error) {
                          console.error('Error downloading NTQ document:', error);
                          const errorMessage = error.message || 'Failed to download NTQ document';
                          toast.error(errorMessage);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={styles.documentPdfIcon}>
                        <span className={styles.pdfText}>PDF</span>
                      </div>
                      <div className={styles.documentContent}>
                        <div className={styles.documentTitle}>Notice To Quit</div>
                      </div>
                    </div>
                    {/* Laravel: Action buttons below card (Change file, Remove) */}
                    {hasPermission('rents.notice_to_quit.manage') && (
                      <div className={styles.documentActions}>
                        <a
                          href={`/rents/${latestRent.id}/notice-to-quit/uploadFile`}
                          className={styles.hiddenPrintLink}
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/rents/${latestRent.id}/notice-to-quit/uploadFile`, '_blank');
                          }}
                        >
                          <div className={`${styles.documentAction} ${styles.documentActionLeft} ${styles.documentActionEdit} ${styles.hiddenPrint}`}>
                            Change file
                          </div>
                        </a>
                        <button
                          className={`${styles.documentAction} ${styles.documentActionRight} ${styles.documentActionDelete} ${styles.hiddenPrint}`}
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to remove?')) {
                              try {
                                // Laravel: DELETE /{rent}/notice-to-quit/delete
                                // Only deletes the file, sets file = null, keeps NTQ record
                                await rentsNoticeToQuitService.deleteFile(latestRent.id);
                                toast.success('NTQ document removed successfully');
                                loadGuardian();
                              } catch (error) {
                                console.error('Error deleting NTQ document:', error);
                                const errorMessage = error.message || 'Failed to delete NTQ document';
                                toast.error(errorMessage);
                              }
                            }
                          }}
                        >
                          Remove
                        </button>
                        {!hasRentEndDatePassed(latestRent) && (
                          <button
                            className={`${styles.documentAction} ${styles.documentActionRight} ${styles.documentActionDelete} ${styles.hiddenPrint}`}
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to remove NTQ? This will delete the NTQ record, not just the file.')) {
                                try {
                                  // Same behavior as "Remove NTQ" button on NTQ manage screen
                                  await rentsService.deleteNoticeToQuit(latestRent.id);
                                  toast.success('NTQ successfully deleted');
                                  loadGuardian();
                                } catch (error) {
                                  console.error('Error deleting NTQ record:', error);
                                  const errorMessage = error.response?.data?.message || error.message || 'Failed to remove NTQ';
                                  toast.error(errorMessage);
                                }
                              }
                            }}
                          >
                            Remove NTQ
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Laravel shows empty panel-group when no NTQ documents (no text, just empty div)
                null
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGuardianHistory = () => {
    if (!guardian) return null;

    // Filter data
    const filteredRents = guardian.rents?.filter(rent => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        rent.room?.property?.name?.toLowerCase().includes(searchLower) ||
        rent.room?.property?.property_id?.toLowerCase().includes(searchLower) ||
        rent.room?.property?.postcode?.toLowerCase().includes(searchLower) ||
        rent.room?.name?.toLowerCase().includes(searchLower) ||
        rent.status?.toLowerCase().includes(searchLower)
      );
    }) || [];

    // Sort data
    const sortedRents = [...filteredRents].sort((a, b) => {
      if (!sortField) return 0;

      let aValue, bValue;

      switch (sortField) {
        case 'property':
          aValue = a.room?.property?.name || '';
          bValue = b.room?.property?.name || '';
          break;
        case 'property_id':
          aValue = a.room?.property?.property_id || '';
          bValue = b.room?.property?.property_id || '';
          break;
        case 'postcode':
          aValue = a.room?.property?.postcode || '';
          bValue = b.room?.property?.postcode || '';
          break;
        case 'room':
          aValue = a.room?.name || '';
          bValue = b.room?.name || '';
          break;
        case 'date_start':
          aValue = new Date(a.date_start || 0);
          bValue = new Date(b.date_start || 0);
          break;
        case 'date_end':
          aValue = new Date(a.date_end || 0);
          bValue = new Date(b.date_end || 0);
          break;
        case 'guardian_officer':
          aValue = `${a.guardian_officer?.first_name || ''} ${a.guardian_officer?.last_name || ''}`.trim();
          bValue = `${b.guardian_officer?.first_name || ''} ${b.guardian_officer?.last_name || ''}`.trim();
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        default:
          return 0;
      }

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      // Handle date comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return 0;
    });

    const totalEntries = sortedRents.length;
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const paginatedRents = sortedRents.slice(startIndex, endIndex);

    return (
      <div className={styles.panelGroup}>
        <div className={styles.panelTitle}>
          <h3>Guardian history</h3>
        </div>
        
        {/* DataTable Controls */}
        <div className={styles.dataTableControls}>
          <div className={styles.showEntries}>
            <label>Show</label>
            <select 
              value={entriesPerPage} 
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.entriesSelect}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>entries</span>
          </div>
          
          <div className={styles.searchBox}>
            <label>Search:</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
              placeholder=""
            />
          </div>
        </div>

        <div className={styles.dataTableWrapper}>
          <table 
            id="datatable-guardian-history" 
            className={`${styles.table} ${styles.tableHover}`}
            data-guardian={guardian.id}
          >
            <thead>
              <tr>
                {/* Laravel column order: ID (rent ID), Property, ID (property_id), Postcode, Room, From, To, GO, Status, Licences, Actions */}
                <th>ID</th>
                <th className={styles.sorting} onClick={() => handleSortTable('property')}>
                  Property <i className={`fa fa-sort${sortField === 'property' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('property_id')}>
                  ID <i className={`fa fa-sort${sortField === 'property_id' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('postcode')}>
                  Postcode <i className={`fa fa-sort${sortField === 'postcode' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('room')}>
                  Room <i className={`fa fa-sort${sortField === 'room' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('date_start')}>
                  From <i className={`fa fa-sort${sortField === 'date_start' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('date_end')}>
                  To <i className={`fa fa-sort${sortField === 'date_end' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('guardian_officer')}>
                  GO <i className={`fa fa-sort${sortField === 'guardian_officer' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th className={styles.sorting} onClick={() => handleSortTable('status')}>
                  Status <i className={`fa fa-sort${sortField === 'status' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                </th>
                <th>Licences</th>
                {hasPermission('rents.dates.edit') && (
                  <th className={styles.jsShowActions}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(paginatedRents) && paginatedRents.map((rent) => (
                <tr key={rent.id}>
                  {/* Laravel column order: ID (rent ID), Property, ID (property_id), Postcode, Room, From, To, GO, Status, Licences, Actions */}
                  <td>{rent.id}</td>
                  <td>
                    <a 
                      href={`/properties/${rent.room?.property?.id}`} 
                      className={styles.propertyLink}
                      onClick={(e) => {
                        e.preventDefault();
                        handleViewProperty(rent.room?.property?.id);
                      }}
                    >
                      {rent.room?.property?.name || '-'}
                    </a>
                        </td>
                  <td>{rent.room?.property?.property_id || '-'}</td>
                  <td>{rent.room?.property?.postcode || '-'}</td>
                  <td>{rent.room?.name || '-'}</td>
                  <td>{formatDate(rent.date_start) === 'Not provided' ? '' : formatDate(rent.date_start)}</td>
                  <td>{formatDate(rent.date_end) === 'Not provided' ? '' : formatDate(rent.date_end)}</td>
                  <td>{rent.guardian_officer?.first_name} {rent.guardian_officer?.last_name || ''}</td>
                  <td>
                    <span className={getRentStatusClass(rent.status, styles)}>
                      {getRentStatusLabel(rent.status)}
                    </span>
                  </td>
                  <td>
                    {/* Laravel Logic: Show Licence button ONLY if signed_license_agreement (filename) exists */}
                    {rent.signed_license_agreement ? (
                      <>
                        <button 
                          type="button"
                          className={styles.btnLicence}
                          onClick={async (e) => {
                            if (e) {
                              e.preventDefault();
                              e.stopPropagation();
                            }
                            await handleViewLicence(rent.id);
                            return false;
                          }}
                          style={{
                            backgroundColor: '#87CEEB',
                            color: 'white',
                            border: '1px solid #87CEEB',
                            padding: '4px 8px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            marginRight: '4px',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Licence
                        </button>
                        <button 
                          className={styles.btnManage}
                          onClick={() => handleManageLicence(rent.id)}
                          style={{
                            backgroundColor: '#1e3a8a',
                            color: 'white',
                            border: '1px solid #1e3a8a',
                            padding: '4px 8px',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          Manage
                        </button>
                      </>
                    ) : (
                      <button 
                        className={styles.btnManage}
                        onClick={() => handleManageLicence(rent.id)}
                        style={{
                          backgroundColor: '#1e3a8a',
                          color: 'white',
                          border: '1px solid #1e3a8a',
                          padding: '4px 8px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        Manage
                      </button>
                    )}
                        </td>
                  {hasPermission('rents.dates.edit') && (
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {/* Edit dates button - Laravel: show if latest_rent has signed_license_agreement */}
                        {guardian.latest_rent &&
                         guardian.latest_rent.signed_license_agreement && (
                          <button
                            className={styles.btnEditDates}
                            onClick={() => window.open(`/rents/${rent.id}/dates/edit`, '_blank')}
                            style={{
                              backgroundColor: '#337ab7',
                              color: 'white',
                              border: '1px solid #337ab7',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Edit Dates"
                          >
                            Edit dates
                          </button>
                        )}

                        {/* Restore button - Only if rent status is "failed" AND it's the latest rent */}
                        {hasPermission('rents.edit') &&
                         guardian.latest_rent && 
                         guardian.latest_rent.id === rent.id && 
                         rent.status === 'failed' && (
                          <button 
                            className={styles.btnRestore}
                            onClick={() => handleRestoreRent(rent.id)}
                            style={{
                              backgroundColor: '#d9534f',
                              color: 'white',
                              border: '1px solid #d9534f',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Restore Rent"
                          >
                            Restore
                          </button>
                        )}

                        {/* Finish button - Only if latest rent AND this is the latest rent AND status is active */}
                        {hasPermission('rents.edit') &&
                         guardian.latest_rent &&
                         guardian.latest_rent.id === rent.id &&
                         (rent.status === 1 || rent.status === 'active') && (
                          <button
                            className={styles.btnFinish}
                            onClick={() => handleEndRent(rent.id)}
                            style={{
                              backgroundColor: '#d9534f',
                              color: 'white',
                              border: '1px solid #d9534f',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Finish Rent"
                          >
                            Finish
                          </button>
                        )}

                        {/* Change rent button - Only if guardian can change rent AND it's the latest ACTIVE rent (not ended/failed) */}
                        {/* Uses canHaveChangedPaymentsOnRent which normalizes old status IDs (14-19) to Laravel IDs (1-13) */}
                        {hasPermission('rents.payments.edit') &&
                         guardian.latest_not_ended_or_failed_rent &&
                         guardian.latest_not_ended_or_failed_rent.id === rent.id &&
                         canHaveChangedPaymentsOnRent(guardian.status?.id) && (
                          <button
                            className={styles.btnChangeRent}
                            onClick={() => handleChangeRent(rent.room?.id || rent.room_id)}
                            style={{
                              backgroundColor: '#5bc0de',
                              color: 'white',
                              border: '1px solid #5bc0de',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Change Rent"
                          >
                            Change rent
                          </button>
                        )}

                        {/* Manage Officer button - Only if guardian can change rent AND it's the latest ACTIVE rent (not ended/failed) */}
                        {/* Uses canHaveChangedPaymentsOnRent which normalizes old status IDs (14-19) to Laravel IDs (1-13) */}
                        {hasPermission('rents.payments.edit') &&
                         guardian.latest_not_ended_or_failed_rent &&
                         guardian.latest_not_ended_or_failed_rent.id === rent.id &&
                         canHaveChangedPaymentsOnRent(guardian.status?.id) && (
                          <button
                            className={styles.btnManageOfficer}
                            onClick={() => handleManageOfficer(rent.id)}
                            style={{
                              backgroundColor: '#5bc0de',
                              color: 'white',
                              border: '1px solid #5bc0de',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Manage Officer"
                          >
                            Manage Officer
                          </button>
                        )}

                        {/* Payments button - Always visible if permission exists */}
                        {hasPermission('payments.list') && (
                          <button
                            className={styles.btnPayments}
                            onClick={() => handleViewPayments(rent.id)}
                            style={{
                              backgroundColor: '#5bc0de',
                              color: 'white',
                              border: '1px solid #5bc0de',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="View Payments"
                          >
                            Payments
                          </button>
                        )}

                        {/* Manage NTQ button - Only if latest rent AND this is the latest rent AND notice_to_quit exists AND rent is active */}
                        {hasPermission('rents.notice_to_quit.manage') &&
                         guardian.latest_rent &&
                         guardian.latest_rent.id === rent.id &&
                         rent.notice_to_quit &&
                         (rent.status === 1 || rent.status === 'active') && (
                          <button
                            className={styles.btnManageNTQ}
                            onClick={() => window.open(`/rents/${rent.id}/notice-to-quit/edit`, '_blank')}
                            style={{
                              backgroundColor: '#337ab7',
                              color: 'white',
                              border: '1px solid #337ab7',
                              padding: '4px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                            title="Manage NTQ"
                          >
                            Manage NTQ
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                      </tr>
              )) || (
                <tr>
                  <td colSpan={hasPermission('rents.dates.edit') ? 11 : 10} style={{ textAlign: 'center', padding: '20px' }}>
                    No guardian history found.
                        </td>
                      </tr>
              )}
                    </tbody>
                  </table>
                </div>

        {/* Pagination */}
        <div className={styles.paginationWrapper}>
          <div className={styles.paginationInfo}>
            Showing {startIndex + 1} to {Math.min(endIndex, totalEntries)} of {totalEntries} entries
          </div>
          <div className={styles.paginationControls}>
            <button 
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              style={{
                backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                color: currentPage === 1 ? '#6c757d' : '#1e3a8a',
                border: '1px solid #dee2e6',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                marginRight: '4px'
              }}
            >
              Previous
            </button>
            <button 
              className={styles.paginationBtn}
              style={{
                backgroundColor: '#14b8a6',
                color: 'white',
                border: '1px solid #14b8a6',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '4px'
              }}
            >
              1
            </button>
            <button 
              className={styles.paginationBtn}
              onClick={() => setCurrentPage(Math.min(Math.ceil(totalEntries / entriesPerPage), currentPage + 1))}
              disabled={endIndex >= totalEntries}
              style={{
                backgroundColor: endIndex >= totalEntries ? '#f8f9fa' : 'white',
                color: endIndex >= totalEntries ? '#6c757d' : '#1e3a8a',
                border: '1px solid #dee2e6',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: endIndex >= totalEntries ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderInternalNotes = () => {
    if (!guardian) return null;

    return (
      <div className={`${styles.panelGroup} ${styles.dPrintNone}`}>
        <div className={styles.panelTitle}>
          <h3>Internal notes</h3>
                    </div>
        <div 
          style={{ lineHeight: '1.6' }}
          dangerouslySetInnerHTML={{ 
            __html: guardian.notes 
              ? guardian.notes.replace(/\n/g, '<br />') 
              : 'No internal notes available.' 
          }}
        />
              </div>
    );
  };

  const renderExportSection = () => {
    if (!guardian) return null;

    return (
      <div className={`${styles.panelGroup} ${styles.dPrintNone}`}>
        <div className={styles.panelTitle}>
          <h3>Actions</h3>
        </div>
        <button
          className={`${styles.btnHead} ${styles.btnHeadStandard}`}
          onClick={handleExportDetails}
          type="button"
        >
          Export guardian details <i className="glyphicon glyphicon-triangle-right"></i>
        </button>
      </div>
    );
  };

  if (loading) return <Loader />;
  if (error) return <div className="error-container">Error: {error}</div>;
  if (!guardian) return <div className="error-container">Guardian not found</div>;

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbRow}>
        <span className={styles.breadcrumbMain}>
           <a href="/dashboard" className={styles.hiddenPrintLink} target="_blank" rel="noopener noreferrer">Dashboard</a>
        </span>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbMain}>
          <a href="/guardians" className={styles.hiddenPrintLink} target="_blank" rel="noopener noreferrer">Guardians</a>
        </span>
        <span className={styles.breadcrumbSep}>/</span>
        <span className={styles.breadcrumbActive}>
          {guardian.user?.first_name} {guardian.user?.last_name} ({guardian.unique_guardian_number})
        </span>
        </div>

      {/* Action Buttons */}
      {renderActionButtons()}

      {/* Guardian Overview */}
      {renderGuardianOverview()}

      {/* Email Preferences */}
      {renderEmailPreferences()}

      {/* Employment History */}
      {renderEmploymentHistory()}

      {/* Current Property */}
      {renderCurrentProperty()}

      {/* Associated Documents */}
      {renderAssociatedDocuments()}

      {/* NTQ Documents */}
      {renderNTQDocuments()}

      {/* Guardian Documents section - Removed to match Laravel (Laravel doesn't have this section) */}
      {/* <GuardianDocuments guardianId={id} /> */}

      {/* Guardian History */}
      {renderGuardianHistory()}

      {/* Internal Notes */}
      {renderInternalNotes()}

      {/* Export Section */}
      {renderExportSection()}

      {/* Modals */}
      {showMedicalModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseMedicalModal}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4 className={styles.modalTitle}>Medical History</h4>
                <button type="button" className="close" onClick={handleCloseMedicalModal}>
                  <span>&times;</span>
                </button>
      </div>
              <div className={styles.modalBody}>
                {guardian.medical_condition_description || 'No medical history details available.'}
              </div>
            </div>
          </div>
                    </div>
                  )}

      {showCriminalModal && (
        <div 
          className="modal fade show" 
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCloseCriminalModal}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalContent}>
              <div className={styles.modalHeader}>
                <h4 className={styles.modalTitle}>Criminal Record</h4>
                <button type="button" className="close" onClick={handleCloseCriminalModal}>
                  <span>&times;</span>
                </button>
                </div>
              <div className={styles.modalBody}>
                {guardian.criminal_record_description || 'No criminal record details available.'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Employment Details Modal */}
      {showEmploymentModal && selectedEmployment && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={handleCloseEmploymentModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '6px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '15px 20px',
                borderBottom: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h4 style={{ margin: 0, fontSize: '18px', fontWeight: '500' }}>
                Employment Details - {selectedEmployment.company_name}
              </h4>
              <button
                type="button"
                onClick={handleCloseEmploymentModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#999',
                  padding: 0,
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px' }}>
              <div className="row">
                <div className="col-md-6">
                  <dl style={{ marginBottom: 0 }}>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Company Name:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>{selectedEmployment.company_name}</dd>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Position:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>{selectedEmployment.position || 'Not specified'}</dd>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Start Date:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>{formatDate(selectedEmployment.date_start)}</dd>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>End Date:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>
                      {selectedEmployment.still_in_employment ? 'Still working there' : formatDate(selectedEmployment.date_end)}
                    </dd>
                  </dl>
                </div>
                <div className="col-md-6">
                  <dl style={{ marginBottom: 0 }}>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Salary:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>
                      {selectedEmployment.salary ? `£${selectedEmployment.salary.toLocaleString()}` : 'Not specified'}
                    </dd>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Employment Status:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>
                      {selectedEmployment.still_in_employment ? 'Current' : 'Previous'}
                    </dd>
                    <dt style={{ fontWeight: 'bold', marginBottom: '5px' }}>Duration:</dt>
                    <dd style={{ marginBottom: '15px', marginLeft: 0 }}>
                      {selectedEmployment.date_start ?
                        (() => {
                          const start = new Date(selectedEmployment.date_start);
                          const end = selectedEmployment.still_in_employment ? new Date() :
                            (selectedEmployment.date_end ? new Date(selectedEmployment.date_end) : new Date());
                          const diffTime = Math.abs(end - start);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          const years = Math.floor(diffDays / 365);
                          const months = Math.floor((diffDays % 365) / 30);
                          return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
                        })() : 'Not calculated'
                      }
                    </dd>
                  </dl>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '15px 20px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}
            >
              <button
                type="button"
                onClick={handleCloseEmploymentModal}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: '1px solid #6c757d',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  handleCloseEmploymentModal();
                  handleEditEmploymentHistory(selectedEmployment.id);
                }}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: '1px solid #007bff',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Edit Employment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Employment Modal */}
      {showAddEmploymentModal && (
        <AddEmploymentModal
          isOpen={showAddEmploymentModal}
          onClose={() => setShowAddEmploymentModal(false)}
          guardianId={id}
          onSuccess={() => {
            loadGuardian();
          }}
        />
      )}
    </div>
  );
};

export default GuardianShow;